import type { WhatsappNumber } from '@store/slices/settingsSlice.tsx';

export interface WhatsAppNumberEntry {
  label: string;
  phone: string;
}

export type WhatsappConfigSource = 'settings' | 'legacy';

export interface ResolvedWhatsappConfig {
  source: WhatsappConfigSource;
  numbers: WhatsAppNumberEntry[];
  message?: string;
}

interface ResolveWhatsappConfigOptions {
  settingsNumbers: WhatsappNumber[] | null | undefined;
  settingsMessage: string | null | undefined;
  legacyNumbers: Record<string, string> | null | undefined;
  legacyMessage: string | null | undefined;
}

const sanitizePhone = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  return value.replace(/[^+\d]/g, '');
};

const normalizeLabel = (value: string | null | undefined, fallback: string): string => {
  const trimmed = value?.trim();
  if (trimmed) {
    return trimmed;
  }
  return fallback;
};

const sanitizeMessage = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ?? '';
};

export const normalizeWhatsappFromRecord = (
  record: Record<string, string> | undefined | null,
): WhatsAppNumberEntry[] => {
  if (!record) {
    return [];
  }

  return Object.entries(record)
    .map(([label, phone]) => ({
      label: normalizeLabel(label, 'WhatsApp'),
      phone: sanitizePhone(phone),
    }))
    .filter((entry) => Boolean(entry.phone));
};

export const normalizeWhatsappFromSettings = (
  entries: WhatsappNumber[] | null | undefined,
): WhatsAppNumberEntry[] => {
  if (!entries || entries.length === 0) {
    return [];
  }

  return entries
    .map((entry, index) => ({
      label: normalizeLabel(entry.label, `WhatsApp ${index + 1}`),
      phone: sanitizePhone(entry.phone_e164 ?? ''),
    }))
    .filter((entry) => Boolean(entry.phone));
};

export const parseWhatsappNumbersFromText = (
  text: string | null | undefined,
): WhatsAppNumberEntry[] => {
  if (!text) {
    return [];
  }

  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [rawLabel, ...rest] = line.split(':');
      const label = rest.length > 0 ? rawLabel : `WhatsApp ${index + 1}`;
      const phone = rest.length > 0 ? rest.join(':') : rawLabel;
      return {
        label: normalizeLabel(label, `WhatsApp ${index + 1}`),
        phone: sanitizePhone(phone),
      };
    })
    .filter((entry) => Boolean(entry.phone));
};

export const serializeWhatsappEntries = (entries: WhatsAppNumberEntry[]): string =>
  entries
    .map((entry) => `${entry.label}: ${entry.phone}`)
    .join('\n');

export const hasWhatsappEntries = (entries: WhatsAppNumberEntry[] | null | undefined): boolean =>
  Boolean(entries && entries.length > 0);

export const resolveWhatsappConfig = ({
  settingsNumbers,
  settingsMessage,
  legacyNumbers,
  legacyMessage,
}: ResolveWhatsappConfigOptions): ResolvedWhatsappConfig => {
  const normalizedSettings = normalizeWhatsappFromSettings(settingsNumbers ?? null);
  const normalizedLegacy = normalizeWhatsappFromRecord(legacyNumbers ?? null);

  const sanitizedSettingsMessage = sanitizeMessage(settingsMessage);
  const sanitizedLegacyMessage = sanitizeMessage(legacyMessage);

  const hasSettings = normalizedSettings.length > 0;
  const source: WhatsappConfigSource = hasSettings ? 'settings' : 'legacy';
  const numbers = source === 'settings' ? normalizedSettings : normalizedLegacy;

  let message = source === 'settings' ? sanitizedSettingsMessage : sanitizedLegacyMessage;
  if (!message) {
    message = source === 'settings' ? sanitizedLegacyMessage : sanitizedSettingsMessage;
  }

  return {
    source,
    numbers,
    message: message || undefined,
  };
};
